import get from 'lodash/get';

import { createSelector } from 'reselect';
import { RootState } from 'store/types';
import { oc } from 'ts-optchain';
import { AllFacilities, Facility, NearestFacility } from './types';

export const liveCareSelector = (state: RootState) => state.liveCare;

export const allFacilitiesSelector = (state: RootState): AllFacilities =>
  get(state, 'liveCare.allFacilities', []);
export const geolocationCoordinatesSelector = (state: RootState) =>
  get(state, 'liveCare.searchGeocode', {});
export const isFetchingSelector = (state: RootState) => get(state, 'liveCare.isFetching', {});
export const locationSearchSuccessSelector = (state: RootState) =>
  get(state, 'liveCare.locationSearchSuccess', {});
export const searchText = (state: RootState): AllFacilities =>
  get(state, 'liveCare.searchLocation', '');
export const liveCareErrorSelector = createSelector(
  liveCareSelector,
  liveCare => liveCare?.error || undefined
);

const SLC_COORDINATES = {
  latitude: 40.768047,
  longitude: -111.888381
};

export const SLC_CITY_NAME = 'Salt Lake City';

export const googleGeolocationCoordinatesSelector = createSelector(
  [geolocationCoordinatesSelector],
  geoLocation => {
    const coords = oc(geoLocation).results[0].geometry.location();

    // Return SLC Coordinates if not found
    if (!coords) {
      return SLC_COORDINATES;
    }

    return {
      longitude: coords.lng,
      latitude: coords.lat
    };
  }
);

export const googleGeolocationNameSelector = createSelector(
  [geolocationCoordinatesSelector],
  geoLocation => {
    const address = get(geoLocation, 'results[0].formatted_address', null);

    if (!address) {
      return SLC_CITY_NAME;
    }

    return address;
  }
);

export const instacaresSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility[] => get(facilities, 'instacares', [])
);

export const kidscaresSelector = createSelector([allFacilitiesSelector], (facilities): Facility[] =>
  get(facilities, 'kidscares', [])
);

export const emergencyRoomsSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility[] => get(facilities, 'emergencyRooms', [])
);

export const nearestInstacareSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility => get(facilities, 'nearestInstacare')
);

export const nearestKidscareSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility => get(facilities, 'nearestKidscare')
);

export const nearestEmergencyRoomSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility => get(facilities, 'nearestEmergencyRoom')
);

export const nearestOpenInstacareSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility => get(facilities, 'nearestOpenInstacare', {})
);

export const nearestOpenKidscareSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility => get(facilities, 'nearestOpenKidscare', {})
);

export const nearestOpenEmergencyRoomSelector = createSelector(
  [allFacilitiesSelector],
  (facilities): Facility => get(facilities, 'nearestOpenEmergencyRoom', {})
);

export const nearestOpenFacilitiesSelector = createSelector(
  [nearestOpenInstacareSelector, nearestOpenEmergencyRoomSelector, nearestOpenKidscareSelector],
  (instcare, emergency, kidscare): NearestFacility[] => {
    return [
      { type: 'instacares', icon: 'location-city', label: 'InstaCare', ...instcare },
      { type: 'emergencyRooms', icon: 'local-hospital', label: 'Emergency Room', ...emergency },
      { type: 'kidscares', icon: 'child-care', label: 'KidsCare', ...kidscare }
    ];
  }
);
